import { useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { CheckoutV2Configuration } from "@jti/commerce";
import type { Address } from "@jti/fe-common/types";
import { useSelector } from "@jti/store/reduxHooks";
import { useGetProfileQuery } from "@jti/store/slices/profileApiSlice";
import { formatPhoneNumber, useApplyAddressOverrides } from "@jti/ui";

import { FISCAL_CODE_ATTRIBUTE_KEY } from "@checkout-v2/features/OrderAttributes/const";
import { telephoneFieldFactory } from "@checkout-v2/formFields";
import { shippingMethodFieldFactory } from "@checkout-v2/formFields/factories/shippingMethodCodeFieldFactory";
import { ShippingDataOverview } from "@checkout-v2/shared/components/ShippingDataOverview";
import { useDeliveryOptions } from "@checkout-v2/shared/hooks/useDeliveryOptions";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";
import { useShippingFormDataContext } from "@checkout-v2/shared/providers/ShippingFormDataProvider";

import { getShippingAddressByShippingMethodType } from "./utils/shippingFormOverviewAddressUtils";

const getEnteredFiscalCode = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  return "";
};

export const ShippingFormDataOverview = () => {
  const config = useConfig();
  const { formFieldsConfig } = useShippingFormDataContext();
  const { applyAddressOverrides } = useApplyAddressOverrides();
  const checkoutConfiguration: CheckoutV2Configuration = useCheckoutV2Configuration();

  const { data: profileData } = useGetProfileQuery();

  const { deliveryOptionsData, allDeliveryOptions } = useDeliveryOptions();

  const checkoutData = useSelector((state) => state.checkoutV2.data);

  const selectedDeliveryMethod = useMemo(() => {
    if (checkoutData?.deliveryOption) {
      const matchedOption = allDeliveryOptions.find((option) => option.methodCode === checkoutData.deliveryOption);

      if (matchedOption) {
        return matchedOption;
      }
    }

    return deliveryOptionsData?.methodSelected;
  }, [allDeliveryOptions, checkoutData?.deliveryOption, deliveryOptionsData?.methodSelected]);

  const shippingMethodType = shippingMethodFieldFactory.getSelectedShippingMethodType(
    checkoutData?.shippingMethodCode ?? "",
    checkoutConfiguration
  );

  const shippingAddress: Address | undefined = getShippingAddressByShippingMethodType(shippingMethodType, checkoutData);

  const formattedTelephone = useMemo(() => {
    if (!checkoutData?.telephone) {
      return "";
    }

    const telephoneString = telephoneFieldFactory.toTelephoneString(
      checkoutData?.telephone,
      checkoutData?.shouldUseProfileData ? formFieldsConfig?.telephone?.savedDefaultValue : undefined
    );

    return formatPhoneNumber(telephoneString, config.isInternationalPhoneEnabled, config.PHONE_FORMAT);
  }, [
    checkoutData,
    config.PHONE_FORMAT,
    config.isInternationalPhoneEnabled,
    formFieldsConfig?.telephone?.savedDefaultValue,
  ]);

  const enteredFiscalCode = checkoutData?.orderAttributes?.[FISCAL_CODE_ATTRIBUTE_KEY];
  const profileFiscalCode = profileData?.extra?.[FISCAL_CODE_ATTRIBUTE_KEY];

  const fiscalCode = useMemo(
    () => getEnteredFiscalCode(enteredFiscalCode) || profileFiscalCode?.trim() || "",
    [enteredFiscalCode, profileFiscalCode]
  );

  const overviewData = useMemo(() => {
    if (!checkoutData?.lastName || !shippingAddress || !selectedDeliveryMethod) {
      return null;
    }

    return {
      firstName: checkoutData.firstName ?? "",
      lastName: checkoutData.lastName,
      telephone: formattedTelephone,
      address: applyAddressOverrides(shippingAddress, { provinceCode: profileData?.extra?.province }),
      deliveryOptionName: selectedDeliveryMethod?.methodTitle,
      fiscalCode,
    };
  }, [
    checkoutData,
    shippingAddress,
    selectedDeliveryMethod,
    formattedTelephone,
    applyAddressOverrides,
    profileData,
    fiscalCode,
  ]);

  return overviewData ? <ShippingDataOverview {...overviewData} /> : null;
};
